import { callFunction } from "./functions-client";

// Content reports + admin moderation queue.
export const REPORT_REASONS = ["spam", "scam", "misleading", "harassment", "other"] as const;
export type ReportReason = (typeof REPORT_REASONS)[number];

export type RiskBand = "low" | "medium" | "high";

export interface RiskReason {
  code: string;
  label: string;
  points: number;
}

export interface RiskInfo {
  score: number;
  band: RiskBand;
  reasons?: RiskReason[];
  computed_at: string;
}

export interface Report {
  id: string;
  target_type: string;
  target_id: string;
  target_account_id: string | null;
  reason: string;
  detail: string | null;
  context: string | null;
  status: string;
  resolution: string | null;
  created_at: string;
  reporter_email: string | null;
  risk?: RiskInfo | null;
}

export const reportContent = (input: {
  target_type: "message" | "profile" | "job" | "load";
  target_id: string;
  reason: ReportReason;
  detail?: string;
  context?: string;
}) => callFunction<{ ok: boolean }>("moderation/report", { body: input });

export const listReports = (status = "reported") =>
  callFunction<{ reports: Report[]; counts: Record<string, number> }>(`moderation/list?status=${encodeURIComponent(status)}`, { method: "GET" });

export const resolveReport = (report_id: string, resolution: "dismiss" | "remove" | "suspend") =>
  callFunction<{ ok: boolean }>("moderation/resolve", { body: { report_id, resolution } });

/** Admin: (re)compute the deterministic fraud/risk score for an account (ADR-0019). */
export const computeAccountRisk = (account_id: string) =>
  callFunction<{ risk: RiskInfo & { account_id: string; reasons: RiskReason[] } }>("moderation/risk", {
    body: { account_id },
  });

// Double-brokering flags (trust-safety-fraud-bible).
export interface DoubleBrokeringFlag {
  id: string;
  kind: "rebroker_by_awardee" | "duplicate_posting";
  status: "open" | "confirmed" | "cleared";
  created_at: string;
  load_id: string;
  related_load_id: string | null;
  flagged_account_id: string | null;
  flagged_email: string | null;
  origin: string | null;
  destination: string | null;
  equipment_type: string | null;
}

export const listDoubleBrokeringFlags = (status = "open") =>
  callFunction<{ flags: DoubleBrokeringFlag[]; counts: Record<string, number> }>(`moderation/db-flags?status=${encodeURIComponent(status)}`, { method: "GET" });

export const reviewDoubleBrokeringFlag = (flag_id: string, decision: "confirmed" | "cleared") =>
  callFunction<{ ok: boolean; status: string }>("moderation/db-flag-review", { body: { flag_id, decision } });
